import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Anchor, ArrowRight } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "am_member_token";

export default function MembersLogin() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem(TOKEN_KEY)) navigate("/members", { replace: true });
  }, [navigate]);

  async function login() {
    if (!email || !code) {
      toast.error("Email and member code are both required.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/members/login`, {
        email: email.trim(),
        code: code.trim().toUpperCase(),
      });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      toast.success(`Welcome back, ${res.data.member.full_name.split(" ")[0]}.`);
      navigate("/members", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid email or member code.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-sand flex items-center justify-center px-6" data-testid="member-login-page">
      <div className="w-full max-w-md bg-white border border-[color:var(--am-line)] p-10">
        <div className="flex items-center gap-2 text-navy mb-8">
          <Anchor size={22} strokeWidth={1.5} />
          <span className="font-serif-display text-2xl">Anchor Management</span>
        </div>
        <div className="overline mb-3">Captain's Club</div>
        <h1 className="font-serif-display text-4xl text-navy mb-3 leading-tight">Members' Lounge</h1>
        <p className="text-sm text-[color:var(--am-muted)] leading-relaxed mb-8">
          Enter your registered email and the member code from your welcome email.
        </p>

        <div className="space-y-5">
          <div>
            <Label htmlFor="email" className="overline block mb-2">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && login()}
              className="rounded-none"
              data-testid="member-email-input"
            />
          </div>
          <div>
            <Label htmlFor="code" className="overline block mb-2">Member Code</Label>
            <Input
              id="code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && login()}
              placeholder="AM-XXXXXXXX"
              className="rounded-none font-mono tracking-wider"
              data-testid="member-code-input"
            />
          </div>
          <button
            onClick={login}
            disabled={submitting || !email || !code}
            className="btn-primary w-full justify-center"
            data-testid="member-login-button"
          >
            {submitting ? "Signing in…" : "Enter the Lounge"} <ArrowRight size={14} />
          </button>
        </div>

        <div className="mt-8 text-center text-xs text-[color:var(--am-muted)]">
          Not a member yet?{" "}
          <Link to="/captains-club" className="underline hover:text-navy" data-testid="member-login-club-link">
            Join the Captain's Club
          </Link>
        </div>
      </div>
    </div>
  );
}
